import React, { useEffect, useRef, useState } from 'react';
import { Mic, Square, Pause, Play, X } from 'lucide-react';
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// Helper for dismissible toasts with X button
const showDismissibleToast = (message, options = {}) => {
    return toast((t) => (
        <div className="flex items-center gap-3">
            <span className="flex-1">{message}</span>
            <button
                onClick={() => toast.dismiss(t.id)}
                className="p-1 hover:bg-black/10 dark:hover:bg-white/10 rounded transition-colors"
            >
                <X className="h-4 w-4" />
            </button>
        </div>
    ), { duration: 5000, ...options });
};

const showDismissibleSuccess = (message, options = {}) => {
    return toast.success((t) => (
        <div className="flex items-center gap-3">
            <span className="flex-1">{message}</span>
            <button
                onClick={() => toast.dismiss(t.id)}
                className="p-1 hover:bg-black/10 dark:hover:bg-white/10 rounded transition-colors"
            >
                <X className="h-4 w-4" />
            </button>
        </div>
    ), { duration: 5000, ...options });
};

const showDismissibleError = (message, options = {}) => {
    return toast.error((t) => (
        <div className="flex items-center gap-3">
            <span className="flex-1">{message}</span>
            <button
                onClick={() => toast.dismiss(t.id)}
                className="p-1 hover:bg-black/10 dark:hover:bg-white/10 rounded transition-colors"
            >
                <X className="h-4 w-4" />
            </button>
        </div>
    ), { duration: 8000, ...options });
};

// Audio Visualizer Component
const AudioVisualizer = ({ stream, isPaused }) => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);
    const analyserRef = useRef(null);
    const audioContextRef = useRef(null);

    useEffect(() => {
        if (!stream) return;

        // Create audio context and analyser
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const analyser = audioContext.createAnalyser();
        const source = audioContext.createMediaStreamSource(stream);

        analyser.fftSize = 64;
        analyser.smoothingTimeConstant = 0.8;
        source.connect(analyser);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const draw = () => {
            if (!canvasRef.current) return;

            animationRef.current = requestAnimationFrame(draw);

            analyser.getByteFrequencyData(dataArray);

            const width = canvas.width;
            const height = canvas.height;

            ctx.clearRect(0, 0, width, height);

            // Number of bars to display
            const barCount = 5;
            const barWidth = 3;
            const gap = 2;
            const totalWidth = barCount * barWidth + (barCount - 1) * gap;
            const startX = (width - totalWidth) / 2;

            // Sample bars from the frequency data
            for (let i = 0; i < barCount; i++) {
                const dataIndex = Math.floor((i / barCount) * bufferLength);
                const value = dataArray[dataIndex] || 0;

                // Calculate bar height (min 4px, max full height)
                const minHeight = 4;
                const maxHeight = height - 4;
                let barHeight = isPaused
                    ? minHeight
                    : Math.max(minHeight, (value / 255) * maxHeight);

                // Add slight variation for visual interest
                if (!isPaused && value > 0) {
                    barHeight = Math.max(minHeight, barHeight + Math.random() * 2);
                }

                const x = startX + i * (barWidth + gap);
                const y = (height - barHeight) / 2;

                // Gradient color based on intensity
                const intensity = value / 255;
                const hue = 142; // Green hue
                const saturation = 70 + intensity * 30;
                const lightness = isPaused ? 50 : 45 + intensity * 15;

                ctx.fillStyle = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
                ctx.beginPath();
                ctx.roundRect(x, y, barWidth, barHeight, 1.5);
                ctx.fill();
            }
        };

        draw();

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            if (audioContextRef.current) {
                audioContextRef.current.close();
            }
        };
    }, [stream, isPaused]);

    return (
        <canvas
            ref={canvasRef}
            width={35}
            height={24}
            className="rounded"
        />
    );
};

const AudioRecorder = ({ onRecordingComplete, clearRecording }) => {

    const [isRecording, setIsRecording] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const mediaRecorder = useRef(null);
    const audioChunks = useRef([]);
    const [audioBlob, setAudioBlob] = useState(null);
    const recordingTimerRef = useRef(null);
    const streamRef = useRef(null);
    const [activeStream, setActiveStream] = useState(null);

    // Draggable bar state
    const [barPosition, setBarPosition] = useState({ x: 0, y: 20 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const barRef = useRef(null);
    const [barInitialized, setBarInitialized] = useState(false);

    useEffect(() => {
        if (clearRecording) {
            clearRecording.current = handleClearRecording;
        }
    }, [clearRecording]);

    // Recording timer
    useEffect(() => {
        if (isRecording && !isPaused) {
            recordingTimerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
        } else {
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
            }
        }
        return () => {
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
            }
        };
    }, [isRecording, isPaused]);

    // Center the bar on initial recording start
    useEffect(() => {
        if (isRecording && barRef.current && !barInitialized) {
            const barWidth = barRef.current.offsetWidth;
            const centerX = (window.innerWidth - barWidth) / 2;
            setBarPosition({ x: centerX, y: 20 });
            setBarInitialized(true);
        }
        if (!isRecording) {
            setBarInitialized(false);
        }
    }, [isRecording, barInitialized]);

    // Drag handlers
    const handleMouseDown = (e) => {
        if (barRef.current) {
            setIsDragging(true);
            setDragOffset({
                x: e.clientX - barPosition.x,
                y: e.clientY - barPosition.y
            });
        }
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging && barRef.current) {
                const barRect = barRef.current.getBoundingClientRect();
                const newX = Math.max(0, Math.min(window.innerWidth - barRect.width, e.clientX - dragOffset.x));
                const newY = Math.max(0, Math.min(window.innerHeight - barRect.height, e.clientY - dragOffset.y));
                setBarPosition({ x: newX, y: newY });
            }
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragOffset]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const handleClearRecording = () => {
        setAudioBlob(null);
        audioChunks.current = [];
        setRecordingTime(0);
        setIsPaused(false);
        setActiveStream(null);
    };

    const handleStartRecording = async () => {
        audioChunks.current = [];
        setRecordingTime(0);
        setIsPaused(false);

        try {
            // Check if getUserMedia is supported
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                showDismissibleError('Your browser does not support audio recording. Please use Chrome, Firefox, or Safari.');
                return;
            }

            // Check microphone permission state if available
            if (navigator.permissions && navigator.permissions.query) {
                try {
                    const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
                    if (permissionStatus.state === 'denied') {
                        showDismissibleError('Microphone access blocked. Please enable it in your browser settings.');
                        return;
                    }
                } catch (e) {
                    // Permission API might not be fully supported, continue anyway
                    console.log('Permission query not supported, attempting direct access');
                }
            }

            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    sampleRate: 44100
                }
            });

            streamRef.current = stream;
            setActiveStream(stream);
            mediaRecorder.current = new MediaRecorder(stream);

            setIsRecording(true);
            showDismissibleSuccess('Audio recording started');

            mediaRecorder.current.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunks.current.push(event.data);
                }
            };

            mediaRecorder.current.onstop = () => {
                // Stop all tracks to release microphone
                if (streamRef.current) {
                    streamRef.current.getTracks().forEach(track => track.stop());
                    streamRef.current = null;
                }
                setActiveStream(null);

                const blob = new Blob(audioChunks.current, { type: 'audio/wav' });
                setAudioBlob(blob);
                onRecordingComplete(blob);
            };

            mediaRecorder.current.start();
        } catch (err) {
            console.error('Microphone error:', err);
            setIsRecording(false);

            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                showDismissibleError('Microphone permission denied. Please allow microphone access and try again.');
            } else if (err.name === 'NotFoundError') {
                showDismissibleError('No microphone found. Please connect a microphone and try again.');
            } else if (err.name === 'NotReadableError') {
                showDismissibleError('Microphone is already in use by another application.');
            } else if (err.name === 'SecurityError') {
                showDismissibleError('Recording requires HTTPS. Please check your connection.');
            } else {
                showDismissibleError('Unable to access microphone: ' + err.message);
            }
        }
    };

    const handlePauseRecording = () => {
        if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
            mediaRecorder.current.pause();
            setIsPaused(true);
            showDismissibleToast('Recording paused', { icon: '⏸️' });
        }
    };

    const handleResumeRecording = () => {
        if (mediaRecorder.current && mediaRecorder.current.state === 'paused') {
            mediaRecorder.current.resume();
            setIsPaused(false);
            showDismissibleToast('Recording resumed', { icon: '▶️' });
        }
    };

    const handleStopRecording = () => {
        setIsRecording(false);
        setIsPaused(false);
        if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
            mediaRecorder.current.stop();
            showDismissibleSuccess('Audio recording stopped');
        }
    };

    const handleCancelRecording = () => {
        setIsRecording(false);
        setIsPaused(false);
        setRecordingTime(0);
        audioChunks.current = [];

        if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
            mediaRecorder.current.stop();
        }

        // Stop all tracks to release microphone
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        setActiveStream(null);

        showDismissibleToast('Recording cancelled', { icon: '🗑️' });
    };

    return (
        <>
            {!audioBlob && !isRecording && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleStartRecording}
                        >
                            <Mic className="h-5 w-5" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>Record Audio</TooltipContent>
                </Tooltip>
            )}

            {/* Recording Status Bar - shown when recording, draggable, starts at top center */}
            {isRecording && (
                <div
                    ref={barRef}
                    className={cn(
                        "fixed z-[9999] flex items-center gap-2 px-3 py-2 bg-popover/95 backdrop-blur-sm rounded-full shadow-lg border",
                        isDragging ? "cursor-grabbing" : "cursor-grab"
                    )}
                    style={{
                        left: barPosition.x,
                        top: barPosition.y,
                        userSelect: 'none'
                    }}
                    onMouseDown={handleMouseDown}
                >
                    {/* Recording indicator */}
                    <div className="flex items-center gap-2 px-2">
                        <div className={cn(
                            "w-2.5 h-2.5 rounded-full",
                            isPaused
                                ? "bg-yellow-500"
                                : "bg-destructive animate-pulse"
                        )} />
                        <span className="font-mono text-sm font-medium text-foreground">
                            {formatTime(recordingTime)}
                        </span>
                    </div>

                    {/* Divider */}
                    <div className="w-px h-5 bg-border" />

                    {/* Audio Visualizer */}
                    {activeStream && (
                        <>
                            <div
                                className="flex items-center justify-center"
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                <AudioVisualizer stream={activeStream} isPaused={isPaused} />
                            </div>

                            {/* Divider */}
                            <div className="w-px h-5 bg-border" />
                        </>
                    )}

                    {/* Audio status badge */}
                    <Badge
                        variant="default"
                        className={cn(
                            "gap-1 cursor-default",
                            isPaused
                                ? "bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 hover:bg-yellow-500/20 border-yellow-500/30"
                                : "bg-green-500/15 text-green-600 dark:text-green-400 hover:bg-green-500/20 border-green-500/30"
                        )}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <Mic className="h-3 w-3" />
                        <span>{isPaused ? "Paused" : "Recording"}</span>
                    </Badge>

                    {/* Divider */}
                    <div className="w-px h-5 bg-border" />

                    {/* Pause/Resume button */}
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 rounded-full"
                                onClick={isPaused ? handleResumeRecording : handlePauseRecording}
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                {isPaused ? (
                                    <Play className="h-4 w-4 text-green-500" />
                                ) : (
                                    <Pause className="h-4 w-4 text-yellow-500" />
                                )}
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            {isPaused ? "Resume Recording" : "Pause Recording"}
                        </TooltipContent>
                    </Tooltip>

                    {/* Cancel button */}
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 rounded-full text-muted-foreground hover:text-destructive"
                                onClick={handleCancelRecording}
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>Cancel Recording</TooltipContent>
                    </Tooltip>

                    {/* Stop button */}
                    <Button
                        variant="destructive"
                        size="sm"
                        className="h-8 px-3 rounded-full gap-1.5"
                        onClick={handleStopRecording}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <Square className="h-3 w-3 fill-current" />
                    </Button>
                </div>
            )}
        </>
    );
};

export default AudioRecorder;
